import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Spin } from 'antd';
import { selectCurrentUser, fetchUserThunk } from './api/redux/authSlice';
import { fetchAddresses } from './api/redux/addressSlice';
import { useAppDispatch } from './api/redux/store';
import ScrollToTop from './components/common/ScrollToTop';
import SupportChatWidget from './components/SupportChat/SupportChatWidget';
import VoiceAssistantInstallModal from './components/SupportChat/VoiceAssistantInstallModal';
import DesktopSideNav from './components/common/DesktopSideNav';
import { useSupportChatDeepLink } from './hooks/useSupportChatDeepLink';

const Onboarding = lazy(() => import('./pages/Onboarding'));
const Auth = lazy(() => import('./pages/Auth'));
const BookingHistoryPage = lazy(() => import('./pages/BookingHistoryPage'));
const BookingOrderDetailPage = lazy(() => import('./pages/BookingOrderDetailPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const BookingPage = lazy(() => import('./pages/BookingPage'));
const ExplorePage = lazy(() => import('./pages/ExplorePage'));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AddressesPage = lazy(() => import('./pages/AddressesPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const BottomNavigator = lazy(() => import('./components/common/BottomNavigator'));
const PaymentScreen = lazy(() => import('./pages/PaymentScreen'));
const PaymentCallbackPage = lazy(() => import('./pages/PaymentCallbackPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const AdminManagementPage = lazy(() => import('./pages/AdminManagementPage'));
const BuyAndInstallPage = lazy(() => import('./pages/BuyAndInstallPage'));
const ColdStorageBookingPage = lazy(() => import('./pages/ColdStorageBookingPage'));
const AllHomeNeedsPage = lazy(() => import('./pages/AllHomeNeedsPage'));
const AllServicesPage = lazy(() => import('./pages/AllServicesPage'));
const AllOffersPage = lazy(() => import('./pages/AllOffersPage'));
const LearnHomePage = lazy(() => import('./pages/LearnHomePage'));
const ShlokaSessionBookPage = lazy(() => import('./pages/ShlokaSessionBookPage'));
const ShlokaRecordedLessonsPage = lazy(() => import('./pages/ShlokaRecordedLessonsPage'));
const ShlokaPracticePage = lazy(() => import('./pages/ShlokaPracticePage'));
const ShlokaLessonPlayerPage = lazy(() => import('./pages/ShlokaLessonPlayerPage'));
const ShlokaSessionPage = lazy(() => import('./pages/ShlokaSessionPage'));

// Custom hook for session token
const useSession = () => {
  return localStorage.getItem('access_token');  
};

// Custom hook for user ID
export const useUserId = () => {
  return localStorage.getItem('user_id');
};

// ProtectedRoute for route protection
const ProtectedRoute = ({ children }: { children: React.ReactElement }) => {
  const token = useSession();
  return token ? children : <Navigate to="/login" replace />;
};

// Fixed AdminRoute: redirect non-admins to /home
const AdminRoute = ({ children }: { children: React.ReactElement }) => {
  const user = useSelector(selectCurrentUser);
  if (!user) return <Spin />;
  return user.role === 'admin' || user.role === 'superadmin' ? children : <Navigate to="/home" replace />;
};

const BuyAndInstallRoute = (
  <ProtectedRoute>
    <BuyAndInstallPage />
  </ProtectedRoute>
);

const AllHomeNeedsRoute = (
  <ProtectedRoute>
    <AllHomeNeedsPage />
  </ProtectedRoute>
);

const ColdStorageRoute = (
  <ProtectedRoute>
    <ColdStorageBookingPage />
  </ProtectedRoute>
);

const LearnRoute = (
  <ProtectedRoute>
    <LearnHomePage />
  </ProtectedRoute>
);

const ShlokaBookRoute = (
  <ProtectedRoute>
    <ShlokaSessionBookPage />
  </ProtectedRoute>
);

const ShlokaRecordedRoute = (
  <ProtectedRoute>
    <ShlokaRecordedLessonsPage />
  </ProtectedRoute>
);

const ShlokaPracticeRoute = (
  <ProtectedRoute>
    <ShlokaPracticePage />
  </ProtectedRoute>
);

const ShlokaPlayerRoute = (
  <ProtectedRoute>
    <ShlokaLessonPlayerPage />
  </ProtectedRoute>
);

const ShlokaSessionRoute = (
  <ProtectedRoute>
    <ShlokaSessionPage />
  </ProtectedRoute>
);

const MAIN_APP_PATHS = ['/home', '/explore', '/booking-history', '/favorites', '/settings', '/payment'] as const;

const AppRouter: React.FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const user = useSelector(selectCurrentUser);
  const showBottomNav = MAIN_APP_PATHS.includes(location.pathname as (typeof MAIN_APP_PATHS)[number]);
  const showMainChrome = showBottomNav;
  const token = useSession();
  const userId = useUserId();

  useSupportChatDeepLink(Boolean(token));

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserThunk(userId));
      dispatch(fetchAddresses(userId));
    }
  }, [dispatch, userId]);

  // Redirect admin to /admin if not already there
  useEffect(() => {
    if (user && user.role === 'admin' && location.pathname !== '/admin') {
      //window.location.replace('/admin');
    }
  }, [user, location.pathname]);

  return (
    <div
      className={[
        'app-shell',
        showMainChrome ? 'app-shell--main' : '',
        showBottomNav ? 'app-shell--with-bottom-nav' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {showMainChrome ? <DesktopSideNav /> : null}
      <main className="app-shell__main">
      <ScrollToTop />
      <Suspense fallback={
        <div className="app-loading">
          <Spin size="large" />
          <span className="app-loading-text">Loading...</span>
        </div>
      }>
        <Routes>
          {/* Public routes */}
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/login" element={token ? <Navigate to="/home" replace /> : <Auth />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          {/* Protected routes */}
          <Route path="/home" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/explore" element={<ProtectedRoute><ExplorePage /></ProtectedRoute>} />
          <Route path="/buy-install" element={BuyAndInstallRoute} />
          <Route path="/cold-storage" element={ColdStorageRoute} />
          <Route path="/learn" element={LearnRoute} />
          <Route path="/learn/book/:mode/:id" element={ShlokaBookRoute} />
          <Route path="/learn/recorded" element={ShlokaRecordedRoute} />
          <Route path="/learn/practice" element={ShlokaPracticeRoute} />
          <Route path="/learn/player/:lessonId" element={ShlokaPlayerRoute} />
          <Route path="/learn/session/:bookingId" element={ShlokaSessionRoute} />
          <Route path="/home-needs" element={AllHomeNeedsRoute} />
          <Route path="/all-services" element={<ProtectedRoute><AllServicesPage /></ProtectedRoute>} />
          <Route path="/offers" element={<ProtectedRoute><AllOffersPage /></ProtectedRoute>} />
          <Route path="/bookings" element={<ProtectedRoute><BookingPage /></ProtectedRoute>} />
          <Route path="/booking" element={<ProtectedRoute><BookingPage /></ProtectedRoute>} />
          <Route path="/cart" element={<ProtectedRoute><Navigate to="/bookings" replace /></ProtectedRoute>} />
          <Route path="/booking-history" element={<ProtectedRoute><BookingHistoryPage /></ProtectedRoute>} />
          <Route path="/bookings/:orderId" element={<ProtectedRoute><BookingOrderDetailPage /></ProtectedRoute>} />
          <Route path="/favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
          <Route path="/payment" element={<ProtectedRoute><PaymentScreen /></ProtectedRoute>} />
          <Route path="/payment/callback" element={<PaymentCallbackPage />} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/addresses" element={<ProtectedRoute><AddressesPage /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="/faq" element={<ProtectedRoute><FAQPage /></ProtectedRoute>} />
          <Route path="/earnings" element={<Navigate to="/home" replace />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminManagementPage />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          {/* Fallback: redirect to login */}
          <Route path="/*" element={<Navigate to="/login" replace />} />
        </Routes>
        {showBottomNav && <BottomNavigator />}
        <SupportChatWidget />
        <VoiceAssistantInstallModal isSignedIn={Boolean(token)} />
      </Suspense>
      </main>
    </div>
  );
};

export default AppRouter;
